/* eslint-disable react-hooks/exhaustive-deps */
import { motion } from 'framer-motion';
import { ReactNode, useMemo, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';

import { IconCaptain } from '../Icons';
import { IconInsured } from '../Icons/IconInsured';
import { IconTransfer } from '../Icons/IconTransfer';

import icChange from '@/assets/ic_change--button.svg';
import { Button, Tags, Divider } from '@/components';
import Clubs from '@/components/Clubs';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useGetPlayerPositionById } from '@/hooks/usePlayer';
import useToast from '@/hooks/useToast';
import { useTournament } from '@/hooks/useTournament';
import { useVersus } from '@/hooks/useVersus';
import { Maybe } from '@/types';
import {
  PlayerComponentInField,
  TacticOnField,
  PlayerClub,
  TemplatePlayer,
  PlayerF5,
  EnumTeamContext,
  PlayersOnFieldF5,
  PlayerComponentInFieldF5,
} from '@/types/team';
import {
  getPlayerPositionListOnField,
  cn,
  resolvePlayerPrice,
  formatMillionBalance,
  addAditionalZeroIfDecimal,
} from '@/utils';
import { TEXTS, POSITIONS_LABELS } from '@/utils/constants';

const playerInfoStyle = {
  title: 'text-3xs lg:text-xs font-medium text-gdt-gray-4 leading-[1]',
  content: 'text-sm lg:text-base text-gdt-gray font-semibold leading-[1]',
  wrapper:
    'flex flex-col justify-between flex-1 px-1 text-center space-y-[0.38rem]',
};

// Se genero este componente dentro de este scope porque el de IconPlayerStatus
// Genera un ToolTip, y no se le puede agregar variantes de colores.

export type DetailsPlayerSubmit = {
  player: TemplatePlayer;
  isCaptain: boolean;
  isInsuranced?: boolean;
  willBeDeleted?: boolean;
  toBeSubtitute?: boolean;
  willBeChanged?: boolean;
  willBeCaptain?: boolean;
  willBeTransfer?: boolean;
};

type DefaultProps = {
  playerId?: number;
  playersOnField?: TacticOnField;
  playersOnFieldF5?: PlayersOnFieldF5;
  selectedPlayerOnList?: Maybe<TemplatePlayer>;
  selectedPlayerOnField?: Maybe<PlayerClub | TemplatePlayer | PlayerF5>;
  messageBeforeActions?: ReactNode;
  onSubmitPlayer: (data: DetailsPlayerSubmit) => void;
  isLoading?: boolean;
  context?: EnumTeamContext;
};

type DeleteVariant = {
  variant?: 'delete';
};

type TransferVariant = {
  variant?: 'transfer';
};

type AddVariant = {
  variant?: 'add';
};

type ChangeTacticVariant = {
  variant?: 'change-tactic';
};

type Props =
  | (DefaultProps & DeleteVariant)
  | (DefaultProps & TransferVariant)
  | (DefaultProps & AddVariant)
  | (DefaultProps & ChangeTacticVariant);

export const DetailsPlayer = ({
  onSubmitPlayer,
  variant,
  playerId,
  playersOnField,
  playersOnFieldF5,
  isLoading,
  selectedPlayerOnList,
  selectedPlayerOnField,
  messageBeforeActions,
  context = EnumTeamContext.Build11,
}: Props) => {
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const { userInitialData } = useAuth();
  const toast = useToast();
  const { tournamentMap } = useTournament();
  const { isMobile, isDesktop } = useIsDesktop();
  const isBuildF5 = context === EnumTeamContext.Build5;
  const DELETE_PLAYER_WORDING = isBuildF5 ? 'Eliminar' : 'Eliminar jugador';
  const DESIGNATE_CAPTAIN_WORDING = isBuildF5 ? 'Capitán' : 'Elegir capitán';
  const INSURANCE_PLAYER_WORDING = 'Asegurar';
  const isMyTeamF5 = context === EnumTeamContext.Team5;

  // Selecciono de que lugar voy a tomar los datos del jugador
  // Del listado o de la cancha, porque son 2 objetos totalmente distintos
  const player = useMemo(() => {
    if (playerId) {
      const player = userInitialData?.plantilla.jugadores?.find(
        (player) => player.id === playerId
      );

      if (player) {
        return player;
      } else {
        toast.show(TEXTS.defaultError);
      }
    }
  }, [playerId, userInitialData?.plantilla.jugadores]);

  // verifico si el jugador es suplente para deshabilitar el boton
  const isSubtitueDisableBtn = useMemo(() => {
    if (playersOnField && selectedPlayerOnField) {
      const isSubtitute =
        'substitutes' in playersOnField &&
        playersOnField.substitutes.some((player: PlayerComponentInField) => {
          return player?.props?.data?.id === selectedPlayerOnField?.id;
        });

      return isSubtitute;
    }

    return false;
  }, [playersOnField]);

  const playerIsCaptain = useMemo(() => {
    if (
      playersOnField &&
      player &&
      (selectedPlayerOnList || selectedPlayerOnField)
    ) {
      if (selectedPlayerOnList) {
        return false;
      }

      if (selectedPlayerOnField) {
        // Tomo todas las posiciones en forma de array para luego hacer una busqueda en un array de JSX.Element[]
        const positions = getPlayerPositionListOnField(playersOnField);
        const selectedPlayer = positions.find(
          (p: PlayerComponentInField) => p.props?.data?.id === player.id
        ) as PlayerComponentInField;

        return selectedPlayer?.props?.data?.capitan;
      }
      return false;
    }

    return false;
  }, [player, playersOnField, selectedPlayerOnList, selectedPlayerOnField]);

  const playerIsCaptainF5 = useMemo(() => {
    if (
      playersOnFieldF5 &&
      player &&
      (selectedPlayerOnList || selectedPlayerOnField)
    ) {
      if (selectedPlayerOnList) {
        return false;
      }

      if (selectedPlayerOnField) {
        const allPlayers = [...(playersOnFieldF5?.fieldPlayers ?? [])];

        if (playersOnFieldF5.goalkeeper) {
          allPlayers.push(playersOnFieldF5?.goalkeeper);
        }

        const selectedPlayer = allPlayers.find(
          (p: PlayerComponentInField) => p.props?.data?.id === player.id
        ) as PlayerComponentInField;

        return selectedPlayer?.props?.data?.capitan;
      }
      return false;
    }

    return false;
  }, [player, playersOnFieldF5, selectedPlayerOnList, selectedPlayerOnField]);

  const playerIsInsuranced = useMemo(() => {
    if (
      playersOnFieldF5 &&
      player &&
      (selectedPlayerOnList || selectedPlayerOnField)
    ) {
      if (selectedPlayerOnList) {
        return false;
      }

      if (selectedPlayerOnField) {
        const allPlayers = [...(playersOnFieldF5?.fieldPlayers ?? [])];

        if (playersOnFieldF5.goalkeeper) {
          allPlayers.push(playersOnFieldF5?.goalkeeper);
        }

        const selectedPlayer = allPlayers.find(
          (p: PlayerComponentInFieldF5) => p.props?.data?.id === player.id
        ) as PlayerComponentInFieldF5;

        return selectedPlayer?.props?.data?.seguro;
      }
      return false;
    }
  }, [player, playersOnFieldF5, selectedPlayerOnList, selectedPlayerOnField]);

  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const position = useGetPlayerPositionById(player?.p);
  const statusInTournament =
    player && tournamentStatusMap ? tournamentStatusMap.get(player?.e) : null;
  const team = player && teamsMap ? teamsMap.get(player?.c) : null;

  const { versus } = useVersus(player?.c || 0);

  const iconButtonClass =
    'flex-1 flex items-center px-1 lg:px-3 justify-center text-xs lg:text-sm font-bold lg:h-[42px] h-[46px] py-0';
  const iconButtonClass2 =
    'flex px-3 flex-1 space-x-2 items-center justify-center text-xs lg:text-sm';
  const disabledIconClass = 'bg-gdt-blue text-white border-gdt-blue opacity-30';
  const disabledButtonClass =
    'text-gdt-blue bg-gdt-blue-xl2 border-0 !opacity-100 disabled:border-gdt-blue disabled:bg-[#CCD9FF] hover:shadow-btn-0';

  const displayNextGame =
    versus && userInitialData ? (
      <div className="bg-[#e3eaff4d] flex items-baseline rounded-[0.375rem] py-4 px-4 lg:px-[2rem] mb-[2.03rem]">
        <div className="flex lg:flex-1 items-baseline">
          <div className="flex items-center gap-1 min-w-[7.5rem]">
            <svg
              fill="none"
              height="19"
              viewBox="0 0 18 19"
              width="18"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 2.1875C4.96793 2.1875 1.6875 5.46793 1.6875 9.5C1.6875 13.5321 4.96793 16.8125 9 16.8125C13.0321 16.8125 16.3125 13.5321 16.3125 9.5C16.3125 5.46793 13.0321 2.1875 9 2.1875ZM14.0273 12.875H12.4376C12.3883 12.8749 12.3399 12.8618 12.2973 12.8372C12.2547 12.8125 12.2193 12.777 12.1946 12.7344L11.6272 11.7613C11.5899 11.6976 11.579 11.6219 11.597 11.5503L12.1194 9.44375C12.1299 9.4013 12.1501 9.36189 12.1785 9.32863C12.2069 9.29537 12.2426 9.26918 12.2829 9.25215L13.2708 8.83379C13.3191 8.81332 13.3721 8.80675 13.4239 8.81483C13.4757 8.8229 13.5242 8.8453 13.564 8.87949L15.0275 10.1388C15.0636 10.1699 15.0912 10.2096 15.1078 10.2542C15.1243 10.2988 15.1292 10.347 15.1221 10.394C15.0009 11.2256 14.7104 12.0234 14.2685 12.7382C14.2436 12.7799 14.2082 12.8144 14.166 12.8384C14.1237 12.8624 14.0759 12.875 14.0273 12.875ZM4.72922 8.8366L5.71711 9.25496C5.75738 9.27199 5.79312 9.29818 5.82149 9.33144C5.84987 9.3647 5.8701 9.40412 5.88059 9.44656L6.40301 11.5531C6.42099 11.6247 6.41013 11.7004 6.37277 11.7641L5.80535 12.7344C5.78072 12.777 5.74532 12.8125 5.70268 12.8372C5.66005 12.8618 5.61168 12.8749 5.56242 12.875H3.97266C3.92457 12.875 3.87729 12.8627 3.83531 12.8393C3.79334 12.8158 3.75807 12.782 3.73289 12.7411C3.29103 12.0263 3.00051 11.2284 2.8793 10.3968C2.87217 10.3498 2.87711 10.3017 2.89365 10.257C2.91019 10.2124 2.93779 10.1727 2.97387 10.1416L4.43742 8.8823C4.47704 8.84838 4.52531 8.82614 4.57684 8.81807C4.62837 8.81 4.68113 8.81641 4.72922 8.8366ZM13.7623 5.76957L13.127 7.5759C13.1142 7.61281 13.0939 7.64667 13.0673 7.6753C13.0408 7.70393 13.0085 7.72671 12.9727 7.74219L11.9327 8.1827C11.885 8.20288 11.8326 8.2095 11.7814 8.20181C11.7302 8.19411 11.682 8.17241 11.6423 8.1391L9.66445 6.48008C9.63285 6.45393 9.60735 6.42119 9.58973 6.38414C9.57212 6.34709 9.56283 6.30664 9.5625 6.26562V5.08508C9.56253 5.03882 9.57396 4.99329 9.59579 4.95251C9.61763 4.91173 9.64918 4.87696 9.68766 4.85129L11.1934 3.84793C11.2327 3.8217 11.278 3.80579 11.325 3.80168C11.3721 3.79756 11.4194 3.80537 11.4627 3.82437C12.3291 4.20226 13.098 4.77264 13.7109 5.49219C13.7432 5.52981 13.7649 5.57527 13.7739 5.62398C13.7829 5.67269 13.7789 5.72291 13.7623 5.76957ZM6.80625 3.84793L8.31234 4.85129C8.35082 4.87696 8.38237 4.91173 8.40421 4.95251C8.42604 4.99329 8.43747 5.03882 8.4375 5.08508V6.26562C8.4375 6.30671 8.4285 6.3473 8.41112 6.38453C8.39375 6.42176 8.36844 6.45474 8.33695 6.48113L6.35906 8.14016C6.31937 8.17347 6.27125 8.19517 6.22001 8.20286C6.16877 8.21055 6.11639 8.20394 6.06867 8.18375L5.02734 7.74219C4.99126 7.72691 4.95876 7.70425 4.93194 7.67567C4.90512 7.6471 4.88457 7.61323 4.8716 7.57625L4.23633 5.76992C4.21982 5.72309 4.216 5.67273 4.22526 5.62395C4.23453 5.57517 4.25654 5.52971 4.28906 5.49219C4.90236 4.77191 5.67188 4.20103 6.53906 3.82297C6.58215 3.80454 6.62916 3.79717 6.67583 3.80153C6.72249 3.80589 6.76732 3.82184 6.80625 3.84793ZM7.4148 15.3602L6.72117 13.6133C6.70531 13.5736 6.69871 13.5308 6.70188 13.4881C6.70505 13.4455 6.7179 13.4041 6.73945 13.3672L7.27277 12.4531C7.2974 12.4105 7.33281 12.375 7.37544 12.3503C7.41807 12.3257 7.46644 12.3126 7.5157 12.3125H10.4843C10.5336 12.3126 10.5819 12.3257 10.6246 12.3503C10.6672 12.375 10.7026 12.4105 10.7272 12.4531L11.2609 13.3672C11.2825 13.4041 11.2954 13.4454 11.2986 13.4881C11.3019 13.5307 11.2953 13.5735 11.2795 13.6133L10.5908 15.3595C10.574 15.4022 10.5469 15.4401 10.5121 15.47C10.4772 15.4998 10.4356 15.5207 10.3908 15.5307C9.47688 15.7405 8.52734 15.7405 7.61344 15.5307C7.56899 15.5205 7.52768 15.4997 7.49307 15.47C7.45847 15.4403 7.43161 15.4026 7.4148 15.3602Z"
                fill="#A09EB3"
              />
            </svg>
            <p className="text-gdt-gray-4 text-xs font-medium">
              Próximo partido
            </p>
          </div>
          <p className="uppercase text-xs lg:text-sm text-gdt-blue-header font-semibold leading-[1] relative top-[-4px]">
            VS {versus}
          </p>
        </div>
        <div className="flex-1 lg:hidden text-center mr-2 flex justify-end">
          <Divider
            className="h-[18px] bg-gdt-gray-5 inline-block relative top-[1px]"
            type="vertical"
          />
        </div>
        <div className="flex items-center gap-1 min-w-[75px]">
          <svg
            fill="none"
            height="17"
            viewBox="0 0 16 17"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clipRule="evenodd"
              d="M3.33301 3.83333C2.96482 3.83333 2.66634 4.13181 2.66634 4.5V13.8333C2.66634 14.2015 2.96482 14.5 3.33301 14.5H12.6663C13.0345 14.5 13.333 14.2015 13.333 13.8333V4.5C13.333 4.13181 13.0345 3.83333 12.6663 3.83333H3.33301ZM1.33301 4.5C1.33301 3.39543 2.22844 2.5 3.33301 2.5H12.6663C13.7709 2.5 14.6663 3.39543 14.6663 4.5V13.8333C14.6663 14.9379 13.7709 15.8333 12.6663 15.8333H3.33301C2.22844 15.8333 1.33301 14.9379 1.33301 13.8333V4.5Z"
              fill="#A09EB3"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="M10.6667 1.16663C11.0349 1.16663 11.3333 1.4651 11.3333 1.83329V4.49996C11.3333 4.86815 11.0349 5.16663 10.6667 5.16663C10.2985 5.16663 10 4.86815 10 4.49996V1.83329C10 1.4651 10.2985 1.16663 10.6667 1.16663Z"
              fill="#A09EB3"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="M5.33366 1.16663C5.70185 1.16663 6.00033 1.4651 6.00033 1.83329V4.49996C6.00033 4.86815 5.70185 5.16663 5.33366 5.16663C4.96547 5.16663 4.66699 4.86815 4.66699 4.49996V1.83329C4.66699 1.4651 4.96547 1.16663 5.33366 1.16663Z"
              fill="#A09EB3"
              fillRule="evenodd"
            />
            <path
              clipRule="evenodd"
              d="M1.33301 7.16667C1.33301 6.79848 1.63148 6.5 1.99967 6.5H13.9997C14.3679 6.5 14.6663 6.79848 14.6663 7.16667C14.6663 7.53486 14.3679 7.83333 13.9997 7.83333H1.99967C1.63148 7.83333 1.33301 7.53486 1.33301 7.16667Z"
              fill="#A09EB3"
              fillRule="evenodd"
            />
          </svg>
          <p className="uppercase text-xs font-medium text-gdt-gray-4 mt-[.1rem]">
            {userInitialData.fechaActiva.nombre}
          </p>
        </div>
      </div>
    ) : null;

  const conditionalFields = useMemo(() => {
    return {
      golesVallas: () => {
        const useVallas =
          position === POSITIONS_LABELS.abbreviation.ARQ ||
          position === POSITIONS_LABELS.abbreviation.DEF;

        let value = '0';

        if (useVallas) {
          if (player?.valla) {
            value = `${player.valla}`;
          }
        } else {
          if (player?.gol) {
            value = `${player.gol}`;
          }
        }

        return {
          data: (
            <>
              <p className={playerInfoStyle.title}>
                {useVallas ? 'Valla Invicta' : 'Goles'}
              </p>
              <p className={playerInfoStyle.content}>{value}</p>
            </>
          ),
          smallWrapper: !useVallas,
        };
      },
      goles: () => {
        let label = '';
        let value = '0';

        const isArq = position === POSITIONS_LABELS.abbreviation.ARQ;
        const isDef = position === POSITIONS_LABELS.abbreviation.DEF;
        const isVolOrDel =
          position === POSITIONS_LABELS.abbreviation.VOL ||
          position === POSITIONS_LABELS.abbreviation.DEL;

        // Label resolver
        if (isArq) {
          label = 'Penales Atajados';
        } else if (isDef) {
          label = 'Goles';
        } else if (isVolOrDel) {
          label = 'Goles de penal';
        }

        // Value Resolver
        if (isArq && player?.penAtaj) {
          value = `${player.penAtaj}`;
        } else if (isDef && player?.gol) {
          value = `${player.gol}`;
        } else if (isVolOrDel && player?.golPen) {
          value = `${player.golPen}`;
        }

        return {
          data: (
            <>
              <p className={playerInfoStyle.title}>{label}</p>
              <p className={playerInfoStyle.content}>{value}</p>
            </>
          ),
          smallWrapper: isDef,
        };
      },
      tarjetas: () => {
        const red = player?.roj ?? '0';
        const yellow = player?.ama ?? '0';

        return (
          <>
            <p className={playerInfoStyle.title}>Tarjetas</p>
            <div className="flex gap-1 justify-center">
              <div className="bg-gdt-status-yellow-light text-gdt-status-yellow text-sm px-[0.2rem] py-[0.2rem] leading-[1] font-bold rounded-[2px]">
                {yellow}
              </div>
              <div className="bg-gdt-status-red-light text-gdt-status-red text-sm px-[0.2rem] py-[0.2rem] leading-[1] font-bold rounded-[2px]">
                {red}
              </div>
            </div>
          </>
        );
      },
    };
  }, []);

  const disablingButtonCap =
    isSubtitueDisableBtn || playerIsCaptain || isLoading;
  const disablingButtonCapF5 = playerIsCaptainF5 || isLoading;
  const disablingButtonInsuranced = playerIsInsuranced || isLoading;

  if (player) {
    return (
      <div className="mb-6 lg:mb-0">
        <div className="flex gap-6 items-start px-0 lgpx-[2rem]">
          <div className="max-w-[3.4rem] lg:max-w-[4rem] flex-none">
            <Clubs clubId={player.c} playerId={player.c} type="SHIRT" />
          </div>
          <div className="space-y-3 flex-1">
            <div className="flex space-x-2 items-center">
              <p className="text-gdt-gray font-semibold text-base">
                {player.a}, {player.n}
              </p>
              {(playerIsCaptain || playerIsCaptainF5) && (
                <IconCaptain className="bg-gdt-blue text-white border-gdt-blue" />
              )}
              {playerIsInsuranced && (
                <IconInsured className="bg-gdt-blue-light2 text-gdt-blue" />
              )}
            </div>
            <div className="flex">
              <div>
                <p className="text-xs lg:text-sm font-medium text-gdt-gray-4">
                  {position} - {team?.nombre.toUpperCase()}
                </p>
                {statusInTournament && (
                  <Tags.Alert
                    isUppercase
                    className="mt-3"
                    statusId={statusInTournament.id}
                    textClassName="text-3xs lg:text-xs"
                  >
                    {statusInTournament.nombre}
                  </Tags.Alert>
                )}
              </div>
              <div className="mt-auto ml-auto flex-none">
                <Button
                  className="font-semibold no-underline flex items-center text-sm lg:text-base gap-2 hover:text-gdt-blue"
                  type="button"
                  variant="text"
                  onClick={() => {
                    return setShowMoreInfo((prev) => !prev);
                  }}
                >
                  Ver {showMoreInfo ? 'menos' : 'más'}
                  <FiChevronDown
                    className={cn('text-gdt-blue w-4 h-4 transition-all', {
                      'transform rotate-180': showMoreInfo,
                    })}
                  />
                </Button>
              </div>
            </div>
          </div>
        </div>
        <motion.div
          animate={{
            height: showMoreInfo ? 'auto' : 0,
            marginTop: showMoreInfo ? (isDesktop ? '1.75rem' : '1rem') : 0,
          }}
          className="overflow-hidden"
          initial={false}
        >
          <div>
            {userInitialData?.plantilla.miniFixture &&
              userInitialData?.plantilla?.miniFixture.length > 0 &&
              displayNextGame}
          </div>
          <div className="block space-y-4 px-0 lg:px-[2rem]">
            <div className="flex divide-x divide-gdt-gray-6">
              <div
                className={cn(playerInfoStyle.wrapper, {
                  'flex-1 lg:flex-[0_0_15%]':
                    isMobile && !conditionalFields.golesVallas().smallWrapper,
                  'flex-[0_0_25%]':
                    isMobile && conditionalFields.golesVallas().smallWrapper,
                })}
              >
                <p className={playerInfoStyle.title}>Cotización</p>
                <p className={playerInfoStyle.content}>
                  ${formatMillionBalance(resolvePlayerPrice(player.cot))}
                </p>
              </div>
              {player.pm && (
                <div
                  className={cn(playerInfoStyle.wrapper, {
                    'flex-1 lg:flex-[0_0_25%]': isMobile,
                  })}
                >
                  <p className={playerInfoStyle.title}>Puntos Prom.</p>
                  <p className={playerInfoStyle.content}>
                    {addAditionalZeroIfDecimal(player.pm)}
                  </p>
                </div>
              )}
              <div
                className={cn(playerInfoStyle.wrapper, {
                  'flex-[0_0_20%] lg:flex-[0_0_20%]':
                    conditionalFields.golesVallas().smallWrapper,
                  'flex-[0_0_25%] lg:flex-[0_0_22%]':
                    !conditionalFields.golesVallas().smallWrapper,
                })}
              >
                {conditionalFields.golesVallas().data}
              </div>
              <div
                className={cn(playerInfoStyle.wrapper, {
                  'flex-[0_0_15%] lg:flex-[0_0_22%]':
                    conditionalFields.goles().smallWrapper,
                  'lg:flex-[0_0_28%]': !conditionalFields.goles().smallWrapper,
                })}
              >
                {conditionalFields.goles().data}
              </div>
            </div>
            <hr className="border-top border-gdt-gray-6 opacity-100 lg:opacity-0" />
            <div className="flex divide-x divide-gdt-gray-6">
              <div className={playerInfoStyle.wrapper}>
                <p className={playerInfoStyle.title}>Ranking {position}</p>
                <p className={playerInfoStyle.content}>#{player?.o ?? '0'}</p>
              </div>
              <div
                className={cn(
                  playerInfoStyle.wrapper,
                  'flex-[0_0_20%] lg:flex-1'
                )}
              >
                {conditionalFields.tarjetas()}
              </div>
              <div
                className={cn(
                  playerInfoStyle.wrapper,
                  'flex-[0_0_30%] lg:flex-[10%]'
                )}
              >
                <p className={playerInfoStyle.title}>Partidos Jugados</p>
                <p className={playerInfoStyle.content}>{player.pj}</p>
              </div>
              <div className={cn(playerInfoStyle.wrapper)}>
                <p className={playerInfoStyle.title}>Veces Figura</p>
                <p className={playerInfoStyle.content}>{player.fig || 0}</p>
              </div>
            </div>
          </div>
        </motion.div>
        <div
          className={cn('text-center', {
            'mt-8': showMoreInfo,
            'mt-8 lg:mt-10': !showMoreInfo,
          })}
        >
          {messageBeforeActions && messageBeforeActions}
          {variant === 'delete' && (
            <div className="flex space-x-2 max-h-11">
              <Button
                className={cn(iconButtonClass2, {
                  [disabledButtonClass]: disablingButtonCap,
                })}
                disabled={isLoading}
                type="button"
                variant="secondary"
                onClick={() => {
                  onSubmitPlayer({
                    player,
                    willBeDeleted: true,
                    isCaptain: false,
                  });
                }}
              >
                {DELETE_PLAYER_WORDING}
              </Button>
              {isBuildF5 && (
                <Button
                  className={cn(iconButtonClass2, {
                    [disabledButtonClass]: disablingButtonInsuranced,
                  })}
                  disabled={disablingButtonInsuranced}
                  type="button"
                  variant="primary"
                  onClick={() => {
                    onSubmitPlayer({
                      player,
                      willBeDeleted: false,
                      isCaptain: false,
                      isInsuranced: true,
                    });
                  }}
                >
                  <IconInsured
                    className={cn('bg-white text-gdt-blue border-white', {
                      [disabledIconClass]: disablingButtonInsuranced,
                    })}
                  />
                  <span
                    className={cn({
                      'opacity-30': disablingButtonInsuranced,
                    })}
                  >
                    {INSURANCE_PLAYER_WORDING}
                  </span>
                </Button>
              )}
              <Button
                className={cn(iconButtonClass2, {
                  [disabledButtonClass]:
                    disablingButtonCapF5 || disablingButtonCap,
                })}
                disabled={disablingButtonCapF5 || disablingButtonCap}
                type="button"
                variant="primary"
                onClick={() => {
                  onSubmitPlayer({
                    player,
                    willBeDeleted: false,
                    isCaptain: true,
                  });
                }}
              >
                <IconCaptain
                  className={cn('bg-white text-gdt-blue border-white', {
                    [disabledIconClass]:
                      disablingButtonCapF5 || disablingButtonCap,
                  })}
                />
                <span
                  className={cn({
                    'opacity-30': disablingButtonCapF5 || disablingButtonCap,
                  })}
                >
                  {DESIGNATE_CAPTAIN_WORDING}
                </span>
              </Button>
            </div>
          )}
          {variant === 'add' && (
            <Button
              className="w-full lg:max-w-[17.5rem] max-h-11"
              disabled={isLoading}
              type="button"
              variant="primary"
              onClick={() => {
                onSubmitPlayer({
                  player,
                  willBeDeleted: false,
                  isCaptain: Boolean(playerIsCaptain),
                });
              }}
            >
              Agregar a mi equipo
            </Button>
          )}
          {variant === 'change-tactic' && (
            <div className="flex space-x-4 lg:px-4 max-h-11">
              <Button
                className="py-4 lg:py-3 px-2 flex-1"
                disabled={isLoading}
                type="button"
                variant="secondary"
                onClick={() => {
                  onSubmitPlayer({
                    player,
                    willBeDeleted: false,
                    isCaptain: false,
                    toBeSubtitute: true,
                  });
                }}
              >
                Hacer suplente
              </Button>
              <Button
                className="py-4 lg:py-3 flex-1 space-x-2 flex items-center justify-center px-2"
                disabled={isSubtitueDisableBtn || isLoading}
                type="button"
                variant="primary"
                onClick={() => {
                  onSubmitPlayer({
                    player,
                    willBeDeleted: true,
                    isCaptain: false,
                  });
                }}
              >
                Eliminar del equipo
              </Button>
            </div>
          )}
          {variant === 'transfer' && !isMyTeamF5 && (
            <div className="flex space-x-2 max-h-11 justify-between">
              <Button
                className={cn(`${iconButtonClass} space-x-2`, {
                  [disabledButtonClass]: disablingButtonCap,
                })}
                disabled={disablingButtonCap}
                type="button"
                variant="secondary"
                onClick={() => {
                  onSubmitPlayer({
                    player,
                    willBeCaptain: true,
                    isCaptain: false,
                  });
                }}
              >
                <IconCaptain
                  className={cn('bg-gdt-blue text-white border-gdt-blue', {
                    [disabledIconClass]: disablingButtonCap,
                  })}
                />
                <span
                  className={cn({
                    'opacity-30': disablingButtonCap,
                  })}
                >
                  Capitán
                </span>
              </Button>
              <Button
                className={iconButtonClass}
                disabled={isLoading}
                type="button"
                variant="primary"
                onClick={() => {
                  onSubmitPlayer({
                    player,
                    isCaptain: Boolean(playerIsCaptain),
                    willBeChanged: true,
                  });
                }}
              >
                <img src={icChange} />
                <span>Cambiar</span>
              </Button>
              <Button
                className={iconButtonClass}
                disabled={isLoading}
                type="button"
                variant="primary"
                onClick={() => {
                  onSubmitPlayer({
                    player,
                    isCaptain: Boolean(playerIsCaptain),
                    toBeSubtitute: false,
                  });
                }}
              >
                <IconTransfer color="#FFFFFF" />
                <span>Transferir</span>
              </Button>
            </div>
          )}
          {variant === 'transfer' && isMyTeamF5 && (
            <div className="flex space-x-2 max-h-11 justify-between">
              <Button
                className={cn(`${iconButtonClass} space-x-2`, {
                  [disabledButtonClass]: disablingButtonInsuranced,
                })}
                disabled={disablingButtonInsuranced}
                type="button"
                variant="secondary"
                onClick={() => {
                  onSubmitPlayer({
                    player,
                    willBeDeleted: false,
                    isCaptain: Boolean(playerIsCaptainF5),
                    isInsuranced: true,
                  });
                }}
              >
                <IconInsured
                  className={cn('bg-gdt-blue text-white border-gdt-blue', {
                    ' opacity-30 ': disablingButtonInsuranced,
                  })}
                />
                <span
                  className={cn({
                    'opacity-30': disablingButtonInsuranced,
                  })}
                >
                  {INSURANCE_PLAYER_WORDING}
                </span>
              </Button>
              <Button
                className={cn(`${iconButtonClass} space-x-2`, {
                  [disabledButtonClass]:
                    disablingButtonCapF5 || disablingButtonCap,
                })}
                disabled={disablingButtonCapF5 || disablingButtonCap}
                type="button"
                variant="secondary"
                onClick={() => {
                  onSubmitPlayer({
                    player,
                    willBeCaptain: true,
                    isCaptain: Boolean(playerIsCaptainF5),
                  });
                }}
              >
                <IconCaptain
                  className={cn('bg-gdt-blue text-white border-gdt-blue', {
                    'opacity-30': disablingButtonCapF5,
                  })}
                />
                <span
                  className={cn({
                    'opacity-30': disablingButtonCapF5,
                  })}
                >
                  Capitán
                </span>
              </Button>
              <Button
                className={`${iconButtonClass} lg:space-x-2`}
                disabled={isLoading}
                type="button"
                variant="primary"
                onClick={() => {
                  onSubmitPlayer({
                    player,
                    isCaptain: Boolean(playerIsCaptainF5),
                    toBeSubtitute: false,
                  });
                }}
              >
                <IconTransfer color="#FFFFFF" />
                <span>Transferir</span>
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};
